import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Header from '../../../components/shared/Header/Header';
import {
  useDeclineTrackerJobMutation,
  useGetReferralStrengthQuestionsQuery,
  useGetTrackerJobDetailsQuery,
  useGetTrackerReferUsersQuery,
} from '../../../redux/apis/trackerApi';

import JobInfoCard from './JobInfoCard';
import ReferralProvidersList from './ReferralProvidersList';
import StatusBanner from './StatusBanner';
import StrengthProgressBar from './StrengthProgressBar';

import styles from './TrackerDetail.module.scss';
import SkeletonLoader from './SkeletonLoader';

const TERMINAL_STATUSES = [
  'hired',
  'hired_and_archived',
  'passed',
  'declined',
  'expired',
  'not_your_refer',
  'archived',
];

const TrackerDetail = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const jobId = searchParams.get('jobId');
  const referralId = searchParams.get('referralId');
  const requestId = searchParams.get('requestId');
  const direction = searchParams.get('direction') || 'my';
  const status = searchParams.get('status') || 'new';

  // Fetch opportunity details
  // API returns { job: {...}, candidate: {...} } after our transformResponse
  const {
    data: detailsData,
    isLoading,
    isError,
  } = useGetTrackerJobDetailsQuery({ referralId, requestId, jobId, direction }, { skip: !jobId });

  // Extract job and candidate from the transformed response
  const opportunity = detailsData?.job;
  const candidateData = detailsData?.candidate;
  const firstReferral = opportunity?.referrals?.[0];
  const candidateStatus = firstReferral?.candidate_status || status;
  const isTerminal = TERMINAL_STATUSES.includes(candidateStatus);

  // Fetch referral strength questions (for "sent jobs" tab, non-terminal statuses)
  const shouldFetchStrength = direction === 'their' && !isTerminal && requestId;
  const { data: strengthData } = useGetReferralStrengthQuestionsQuery(
    { referRequestId: requestId },
    { skip: !shouldFetchStrength }
  );

  // Fetch refer users (for "my jobs" tab)
  const shouldFetchReferUsers = direction === 'my' && jobId;
  const { data: referUsersData } = useGetTrackerReferUsersQuery({ jobId }, { skip: !shouldFetchReferUsers });

  // Decline job mutation
  const [declineJob, { isLoading: isDeclining }] = useDeclineTrackerJobMutation();

  const backUrl = `/tracker/${direction === 'my' ? 'my-jobs' : 'sent-jobs'}`;

  const handleDecline = async () => {
    if (!opportunity?.id) return;
    try {
      await declineJob({ jobId: opportunity.id }).unwrap();
      navigate(backUrl);
    } catch (err) {
      console.error('Failed to decline job:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column flex-grow-1" style={{ height: '100vh', overflow: 'hidden' }}>
        <Header title={t('navigation.tracker')} backButton={t('tracker.back', 'Back')} backUrl={backUrl} />
        <SkeletonLoader />
      </div>
    );
  }

  if (isError || !opportunity || !opportunity.id) {
    return (
      <div className="d-flex flex-column flex-grow-1" style={{ height: '100vh', overflow: 'hidden' }}>
        <Header title={t('navigation.tracker')} backButton={t('tracker.back', 'Back')} backUrl={backUrl} />
        <div className={styles.errorContainer}>
          <p>{t('tracker.errorLoadingDetails', 'Failed to load details')}</p>
        </div>
      </div>
    );
  }

  const pageTitle =
    direction === 'my'
      ? `${t('tracker.applicationTo', 'Application to')} ${opportunity.organization_name || ''}`
      : `${t('tracker.referralTo', 'Referral to')} ${opportunity.organization_name || ''}`;

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ height: '100vh', overflow: 'hidden' }}>
      <Header title={t('navigation.tracker')} backButton={t('tracker.back', 'Back')} backUrl={backUrl} />

      <div className={styles.container}>
        <h2 className={styles.pageTitle} data-testid="user-tracker-detail-candidate-name">
          {pageTitle}
        </h2>

        {/* Status banner for terminal statuses */}
        {isTerminal && (
          <StatusBanner status={candidateStatus} direction={direction} referralName={firstReferral?.full_name} />
        )}

        {/* Job card — always show with full info */}
        <JobInfoCard
          opportunity={opportunity}
          direction={direction}
          candidateStatus={candidateStatus}
          onDecline={handleDecline}
          isDeclining={isDeclining}
          firstReferral={firstReferral}
        />

        {/* Strength progress bar (non-terminal only) */}
        {/* {!isTerminal && (
          <StrengthProgressBar
            direction={direction}
            strengthData={strengthData}
            candidateData={candidateData}
            applicationStrengthenQuestions={detailsData?.strengthenQuestions}
            opportunity={opportunity}
            firstReferral={firstReferral}
          />
        )} */}

        {/* Referral providers list */}
        {opportunity.referrals?.length > 0 && (
          <ReferralProvidersList
            referrals={opportunity.referrals}
            direction={direction}
            candidateStatus={candidateStatus}
            currentUserId={firstReferral?.id}
          />
        )}
      </div>
    </div>
  );
};

export default TrackerDetail;
